import { useEffect, useRef } from 'react';

const equipItems = [
  {
    icon: 'ri-table-line',
    title: 'ポーカーマット',
    subtitle: '3卓完備',
    desc:
      '本格的なポーカーマットを完備。ディーラーなしでも快適にプレイができる、ちょうど良い大きさです。最大6名/1卓でプレイ可能。',
    image:
      'https://storage.readdy-site.link/project_files/f9bab69d-2fbc-4411-9906-6fecd905051b/be6a6e6f-946f-453d-842d-9ed184a18d97_unnamed.jpg?v=d6a04b130e7902be21c27004830015cb',
    alt:
      'デュエル店内に設置された6人用ポーカーテーブルとポーカーマット',
    tags: [
      '最大6名/卓',
      'フェルト仕様',
      '3卓完備',
    ],
  },
  {
    icon: 'ri-coin-line',
    title: 'チップ・トランプ',
    subtitle: '自動シャッフル機あり',
    desc:
      'モンテカルロ仕様の本格クレイチップと、文字が大きく見やすいトランプを完備。自動シャッフル気もあり、快適にプレイできます。',
    image:
      'https://static.readdy.ai/image/9f1ed1380ea2c8a902f8bc322070b10e/6d3bab75db7c6aeacce10ded1dbc1d40.png',
    alt:
      'デュエルで利用できるポーカーチップとトランプ',
    tags: [
      'モンテカルロ仕様',
      '見やすいトランプ',
      '本格仕様',
    ],
  },
  {
    icon: 'ri-game-line',
    title: 'ボードゲーム',
    subtitle: 'カタン完備',
    desc:
      'カタン完備。追加希望のゲームは気軽にご連絡ください！',
    image:
      'https://storage.readdy-site.link/project_files/f9bab69d-2fbc-4411-9906-6fecd905051b/f30ec1c2-b2ab-4ea4-a9f0-6da02ffb9ab5_unnamed.jpg?v=5778267d9ca797d84ef9a11dd592899e',
    alt:
      'ポーカーレンタルスペース デュエルで利用できるボードゲーム',
    tags: [
      'カタン完備',
      '希望随時受付中',
    ],
  },
];

function useScrollAnimation(
  ref: React.RefObject<HTMLElement | null>
) {
  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const observer =
      new IntersectionObserver(
        ([entry]) => {
          if (
            entry.isIntersecting
          ) {
            el.classList.add(
              'visible'
            );

            observer.disconnect();
          }
        },
        {
          threshold: 0.1,
        }
      );

    observer.observe(el);

    return () =>
      observer.disconnect();
  }, [ref]);
}

function EquipCard({
  item,
  delay,
}: {
  item: typeof equipItems[0];
  delay: number;
}) {
  const ref =
    useRef<HTMLDivElement>(null);

  useScrollAnimation(
    ref as React.RefObject<HTMLElement>
  );

  return (
    <div
      ref={ref}
      className="fade-in-section bg-[#1a1f2e] rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300 cursor-default"
      style={{
        transitionDelay:
          `${delay}ms`,
      }}
    >
      <div className="w-full h-56 md:h-64 overflow-hidden">
        <img
          src={item.image}
          alt={item.alt}
          loading="lazy"
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-lg">
            <i
              className={`${item.icon} text-[#d4af37] text-xl`}
            />
          </div>

          <div>
            <h3 className="font-noto font-bold text-white text-base">
              {item.title}
            </h3>

            <p className="text-[#d4af37] text-xs font-medium">
              {item.subtitle}
            </p>
          </div>
        </div>

        <p className="text-white/60 text-sm leading-relaxed mb-4 font-noto">
          {item.desc}
        </p>

        <div className="flex flex-wrap gap-2">
          {item.tags.map(
            (tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-[#2a2f3e] text-white/60 text-xs rounded-full font-noto"
              >
                {tag}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function Equipment() {
  const titleRef =
    useRef<HTMLDivElement>(null);

  useScrollAnimation(
    titleRef as React.RefObject<HTMLElement>
  );

  return (
    <section
      id="equipment"
      className="bg-[#0f1419] py-20 md:py-28"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        <div
          ref={titleRef}
          className="fade-in-section text-center mb-14"
        >
          <p className="section-label mb-3">
            EQUIPMENT
          </p>

          <h2 className="font-playfair font-bold text-white text-3xl md:text-4xl mb-4">
            設備
          </h2>

          <p className="text-white/50 font-noto text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            ポーカーテーブル・チップ・トランプなど、
            ポーカーを楽しむために必要な設備を揃えています。
            <br className="hidden sm:block" />
            道具を持参せず、手ぶらでご利用いただけます。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {equipItems.map(
            (item, i) => (
              <EquipCard
                key={
                  item.title
                }
                item={item}
                delay={
                  i * 80
                }
              />
            )
          )}
        </div>
      </div>
    </section>
  );
}